import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import Ticket from './Ticket';
import Header from './Header';
import Footer from './Footer';
import loadScript from './utils/loadScript';

function App() {
    const [tickets, setTickets] = useState([]);
    const [cart, setCart] = useState({});
    const [coupon, setCoupon] = useState('');
    const [debouncedCoupon, setDebouncedCoupon] = useState(coupon);
    const [subtotal, setSubtotal] = useState('0.00');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [street, setStreet] = useState('');
    const [postCode, setPostCode] = useState('');
    const [city, setCity] = useState('');
    const [email, setEmail] = useState('');
    const [emailConfirmation, setEmailConfirmation] = useState('');
    const [termsAccepted, setTermsAccepted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);

    useEffect(() => {
        axios.get('https://ticketingapi.greateight.ch/api/v1/event/lookup/shop')
            .then(response => {
                const eventsData = response.data.events || [];
                setEvents(eventsData);
                if (eventsData.length > 0) {
                    setSelectedEvent(eventsData[0]);
                }
            })
            .catch(error => console.error('Error fetching events:', error));
    }, []);

    useEffect(() => {
        if (!selectedEvent) return;

        axios.get(`https://ticketingapi.greateight.ch/api/v1/ticketoffer/${selectedEvent.id}/lookup`)
            .then(response => {
                setTickets(response.data.ticketOffers || []);
            })
            .catch(error => console.error('Error fetching tickets:', error));
    }, [selectedEvent]);

    useEffect(() => {
        var _paq = window._paq = window._paq || [];
        _paq.push(['trackPageView']);
        _paq.push(['enableLinkTracking']);
        (function () {
            var u = "https://metrics.greateight.ch/";
            _paq.push(['setTrackerUrl', u + 'matomo.php']);
            _paq.push(['setSiteId', '8']);
            var d = document, g = d.createElement('script'), s = d.getElementsByTagName('script')[0];
            g.async = true; g.src = u + 'matomo.js'; s.parentNode.insertBefore(g, s);
        })();
    }, []);

    useEffect(() => {
        calculateSubtotal();
    }, [cart]);

    const updateCart = (id, amount) => {
        setCart((prev) => ({ ...prev, [id]: amount }));
    };

    const formatPrice = (priceInCents) => {
        return (priceInCents / 100).toFixed(2);
    };

    const handleCouponChange = (event) => {
        setCoupon(event.target.value);
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedCoupon(coupon);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [coupon]);

    useEffect(() => {
        if (debouncedCoupon !== null) {
            calculateSubtotal();
        }
    }, [debouncedCoupon]);

    const isFormValid = () => {
        const isEmailValid = email === emailConfirmation && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        const isAtLeastOneProductSelected = Object.values(cart).some(amount => amount > 0);
        const allFieldsFilled = firstName.trim() && lastName.trim() && street.trim() &&
            postCode.trim() && city.trim() && email.trim() && emailConfirmation.trim();

        return allFieldsFilled && isEmailValid && isAtLeastOneProductSelected && termsAccepted;
    };

    const calculateSubtotal = () => {
        if (Object.keys(cart).length === 0 && !coupon) {
            setSubtotal('0.00');
            return;
        }
        axios.post('https://ticketingapi.greateight.ch/api/v1/order/cart/total', {
            items: Object.entries(cart).map(([id, amount]) => ({ id, amount })),
            coupon
        })
            .then(response => {
                setSubtotal(formatPrice(response.data.totalPrice || 0));
            })
            .catch(error => console.error('Error calculating subtotal:', error));
    };

    const handleOrder = () => {
        const orderData = {
            items: Object.entries(cart).map(([id, amount]) => ({ id, amount })),
            coupon,
            firstName,
            lastName,
            street,
            postCode,
            city,
            email
        };

        setIsSubmitting(true);

        axios.post('https://ticketingapi.greateight.ch/api/v1/order/payment/initialize', orderData)
            .then(response => {
                if (response.data.resultStatus.success) {
                    const walleeLightboxUrl = response.data.lightboxScriptUrl;

                    loadScript(walleeLightboxUrl, "wallee-script")
                        .then(() => {
                            window.LightboxCheckoutHandler.startPayment(1, function () {
                                alert('An error occurred during payment initialization.');
                            });
                        })
                        .catch((error) => console.error("Failed to load Wallee script:", error));
                } else {
                    alert(response.data.resultStatus.errorMessage);
                }
            })
            .catch(error => {
                console.error('Error placing order:', error);
                alert('Error placing order:' + error);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <>
            <Header events={events} selectedEvent={selectedEvent} onSelectEvent={setSelectedEvent} />
            <Container style={{ marginTop: '1rem' }}>
                <h1>🎟️ {selectedEvent ? selectedEvent.title : "Event auswählen"}</h1>
                <h6 style={{ color: 'darkslategray'}} dangerouslySetInnerHTML={{ __html: selectedEvent ? selectedEvent.caption : "" }} />
                <Row>
                    <Col lg={8}>
                        <Row className="d-flex align-items-stretch">
                            {tickets.length > 0 ? tickets.map(ticket => (
                                <Col key={ticket.id} xs={12} sm={tickets.length < 3 ? 12 / tickets.length : 6} md={tickets.length < 3 ? 12 / tickets.length : 6} className="mb-3 d-flex">
                                    <Ticket ticket={ticket} onUpdate={updateCart} formatPrice={formatPrice} />
                                </Col>
                            )) : (
                                <Col>
                                    <Alert variant="info">Keine Tickets verfügbar</Alert>
                                </Col>
                            )}
                        </Row>
                    </Col>
                    <Col lg={4}>
                        <div className="sticky-desktop" style={{ top: '100px' }}>
                            <Form>
                                <Card className="shadow-sm bg-light">
                                    <Card.Body>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Rabattcode</Form.Label>
                                            <Form.Control type="text" maxLength="100" placeholder="Rabattcode eingeben" value={coupon} onChange={handleCouponChange} />
                                        </Form.Group>
                                        <h4>Total: CHF {subtotal}</h4>
                                        <Alert variant="success" className="mt-3">* Kinderticket <b>(Unter 12 Jahren)</b> nur gültig mit Begleitperson.</Alert>
                                        <h5 className="mb-3">Lieferadresse</h5>
                                        <Row>
                                            <Col md={6}><Form.Control placeholder="Vorname" className="mb-3" value={firstName} onChange={(e) => setFirstName(e.target.value)} /></Col>
                                            <Col md={6}><Form.Control placeholder="Nachname" className="mb-3" value={lastName} onChange={(e) => setLastName(e.target.value)} /></Col>
                                        </Row>
                                        <Form.Control placeholder="Strasse und Hausnummer" className="mb-3" value={street} onChange={(e) => setStreet(e.target.value)} />
                                        <Row>
                                            <Col md={4}><Form.Control placeholder="PLZ" className="mb-3" value={postCode} onChange={(e) => setPostCode(e.target.value.replace(/\D/g, ''))} /></Col>
                                            <Col md={8}><Form.Control placeholder="Ort" className="mb-3" value={city} onChange={(e) => setCity(e.target.value)} /></Col>
                                        </Row>
                                        <Form.Control placeholder="E-Mail" className="mb-3" value={email} onChange={(e) => setEmail(e.target.value)} />
                                        <Form.Control placeholder="E-Mail bestätigen" className="mb-3" value={emailConfirmation} onChange={(e) => setEmailConfirmation(e.target.value)} />
                                        <Form.Check type="checkbox" label={<>Ich akzeptiere die <a href="https://greateight.ch/ticketshop-agb/" target="_blank" rel="noreferrer">AGB</a></>} checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} className="mb-3" />
                                        <Button variant="primary" disabled={!isFormValid() || isSubmitting} onClick={handleOrder}>
                                            {isSubmitting ? <Spinner size="sm" /> : 'Jetzt bestellen'}
                                        </Button>
                                    </Card.Body>
                                </Card>
                            </Form>
                        </div>
                    </Col>
                </Row>
            </Container>
            <Footer/>
        </>
    );
}

export default App;